import { useEffect, useState } from 'react';
import { AppShell } from '../../components/app/AppShell';
import { useAdminData } from '../../admin/AdminDataContext';
import { AnalyticalRowFieldInput } from '../../components/shared/AnalyticalRowFieldInput';
import { pickObjectTitle } from '../../components/shared/useObjectCard';
import { api, ApiError } from '../../lib/api';
import {
  analyticalTablesApi,
  findReferenceMirrorField,
  type AnalyticalRow,
  type AnalyticalTableDetail,
} from '../../lib/analyticalTablesApi';
import {
  layersApi,
  type EnergyReportKind,
  type LayerDto,
  type LayerFieldDetail,
  type ReportTable,
} from '../../lib/layersApi';
import type { Me } from '../../lib/types';

/** Прототип под тендер по наружному освещению — см. src/lib/featureFlags.ts
 * (reportsPrototype). Единая страница закрывает несколько пунктов чек-листа сразу:
 * генерический экспорт любого слоя в Excel, три отчёта по показаниям электроэнергии
 * (теперь на аналитической таблице, не слое — см. analyticalTablesApi) и сводный
 * отчёт с группировкой по полю для любого слоя. */

const NAV_ITEMS = [
  { label: 'Главная', to: '/app' },
  { label: 'Отчёты', to: '/app/reports' },
  { label: 'Данные и слои', to: '/app/data' },
  { label: 'Задачи', to: '/app/tasks' },
  { label: 'Настройки', to: '/app/profile' },
];

const ENERGY_TABLE_CODE = 'energy_readings';

// 'budget' — «ведомость по электроэнергии с расчётом затрат по областному и
// местному бюджетам» (см. analyticalTablesApi.getBudgetReport) — не входит в
// EnergyReportKind (тот описывает три СРЕЗА одних и тех же показаний;
// ведомость — отдельный расчёт с тарифом и итогами по бюджету), но живёт в
// одном ряду вкладок с ними, поэтому здесь свой объединяющий тип.
type ReportTab = EnergyReportKind | 'budget';

const ENERGY_TABS: { kind: ReportTab; label: string }[] = [
  { kind: 'plan_fact', label: 'План / факт' },
  { kind: 'monthly', label: 'По периодам' },
  { kind: 'consumption', label: 'Потребление' },
  { kind: 'budget', label: 'Ведомость (бюджет)' },
];

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function formatCell(value: string | number | null): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'number') return value.toLocaleString('ru-RU');
  return value;
}

/** Add/edit form for one row of an analytical table — used instead of the
 * hierarchy "Задать родительский объект" picker: a reference field here is
 * just a normal form field, filled in directly when creating the reading, not
 * attached afterwards through a separate relationship-editing step. */
function AnalyticalRowForm({
  table,
  mapId,
  onCancel,
  onSaved,
}: {
  table: AnalyticalTableDetail;
  mapId: string;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [referenceOptionsByField, setReferenceOptionsByField] = useState<Record<string, { id: string; title: string }[]>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const referenceFields = table.fields.filter((f) => f.reference_layer_code);
    if (referenceFields.length === 0) return;
    layersApi.listLayers(mapId).then((layers) => {
      for (const f of referenceFields) {
        const target = layers.find((l) => l.code === f.reference_layer_code);
        if (!target) continue;
        layersApi.getFeatures(target.id).then((fc) => {
          setReferenceOptionsByField((prev) => ({
            ...prev,
            [f.code]: fc.features.map((feat) => ({ id: feat.id, title: pickObjectTitle(feat.properties) })),
          }));
        });
      }
    });
  }, [table, mapId]);

  // Reference fields (e.g. meter_feature_id) have a plain-text sibling (e.g.
  // meter_ref) that exists only to hold a readable label — no reason to make
  // the user retype the name of what they just picked from the dropdown.
  const mirrorFieldCodes = new Set(
    table.fields
      .map((f) => (f.reference_layer_code ? findReferenceMirrorField(table.fields, f)?.code : undefined))
      .filter((code): code is string => Boolean(code)),
  );
  const visibleFields = table.fields.filter((f) => !mirrorFieldCodes.has(f.code));

  const handleFieldChange = (f: (typeof table.fields)[number], v: string) => {
    setValues((prev) => {
      const next = { ...prev, [f.code]: v };
      const mirror = f.reference_layer_code ? findReferenceMirrorField(table.fields, f) : undefined;
      if (mirror) next[mirror.code] = referenceOptionsByField[f.code]?.find((o) => o.id === v)?.title ?? '';
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const attributes: Record<string, unknown> = {};
      for (const f of table.fields) {
        const v = values[f.code];
        if (v) attributes[f.code] = v;
      }
      await analyticalTablesApi.createRow(table.id, attributes);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось сохранить запись.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-3 flex flex-col gap-2 rounded-xl border border-border-default bg-bg-surface-secondary p-3">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {visibleFields.map((f) => (
          <div key={f.code} className="flex flex-col gap-1">
            <label className="text-[11px] font-medium text-text-secondary">{f.name}</label>
            <AnalyticalRowFieldInput
              field={f}
              value={values[f.code] ?? ''}
              onChange={(v) => handleFieldChange(f, v)}
              referenceOptions={referenceOptionsByField[f.code] ?? []}
            />
          </div>
        ))}
      </div>
      {error && <p className="text-[11px] font-medium text-status-danger">{error}</p>}
      <div className="flex justify-end gap-3">
        <button type="button" onClick={onCancel} className="text-[12px] font-medium text-text-secondary hover:text-text-primary">
          Отмена
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="rounded-md bg-action-primary px-3 py-1.5 text-[12px] font-medium text-text-inverse disabled:cursor-not-allowed disabled:opacity-50"
        >
          {saving ? 'Сохраняем…' : 'Добавить запись'}
        </button>
      </div>
    </div>
  );
}

export function ReportsPage() {
  const { mapSettings } = useAdminData();
  const [me, setMe] = useState<Me | null>(null);
  const [layers, setLayers] = useState<LayerDto[]>([]);
  const [analyticalTableList, setAnalyticalTableList] = useState<{ id: string; name: string }[]>([]);
  // "layer:<id>" or "table:<id>" — one <select> covering both kinds.
  const [selection, setSelection] = useState('');
  const [fields, setFields] = useState<LayerFieldDetail[]>([]);
  const [analyticalTable, setAnalyticalTable] = useState<AnalyticalTableDetail | null>(null);
  const [rows, setRows] = useState<AnalyticalRow[]>([]);
  const [addRowOpen, setAddRowOpen] = useState(false);
  const [energyKind, setEnergyKind] = useState<ReportTab>('plan_fact');
  const [groupField, setGroupField] = useState('');
  const [sumField, setSumField] = useState('');
  const [table, setTable] = useState<ReportTable | null>(null);
  const [budgetTotals, setBudgetTotals] = useState<{ budget: string; fact_kwh: number; cost: number }[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.getMe().then(setMe).catch(() => {});
  }, []);

  useEffect(() => {
    if (!mapSettings?.id) return;
    layersApi.listLayers(mapSettings.id).then(setLayers).catch(() => setLayers([]));
    analyticalTablesApi
      .listTables()
      .then((ts) => setAnalyticalTableList(ts.map((t) => ({ id: t.id, name: t.name }))))
      .catch(() => setAnalyticalTableList([]));
  }, [mapSettings?.id]);

  const [kind, selectedId] = selection.split(':') as ['layer' | 'table' | undefined, string | undefined];
  const selectedLayer = kind === 'layer' ? (layers.find((l) => l.id === selectedId) ?? null) : null;
  const isEnergyTable = kind === 'table' && analyticalTable?.code === ENERGY_TABLE_CODE;

  const numericFields = fields.filter(
    (f) => f.data_type !== 'boolean' && f.data_type !== 'dictionary' && f.data_type !== 'date' && f.data_type !== 'datetime',
  );

  const refreshRows = () => {
    if (kind !== 'table' || !selectedId) return;
    analyticalTablesApi
      .listRows(selectedId)
      .then(setRows)
      .catch(() => setRows([]));
  };

  useEffect(() => {
    setTable(null);
    setError(null);
    setGroupField('');
    setSumField('');
    setFields([]);
    setAnalyticalTable(null);
    setRows([]);
    setAddRowOpen(false);
    if (!selectedId) return;
    if (kind === 'layer') {
      layersApi
        .getLayer(selectedId)
        .then((d) => setFields(d.fields))
        .catch(() => setFields([]));
    } else if (kind === 'table') {
      analyticalTablesApi
        .getTable(selectedId)
        .then(setAnalyticalTable)
        .catch(() => setAnalyticalTable(null));
      refreshRows();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection]);

  useEffect(() => {
    if (!selectedId || !isEnergyTable) return;
    setLoading(true);
    setError(null);
    setBudgetTotals(null);
    const request =
      energyKind === 'budget'
        ? analyticalTablesApi.getBudgetReport(selectedId).then((r) => {
            setBudgetTotals(r.totals);
            return r;
          })
        : analyticalTablesApi.getEnergyReport(selectedId, energyKind);
    request
      .then(setTable)
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Не удалось построить отчёт.'))
      .finally(() => setLoading(false));
  }, [selectedId, isEnergyTable, energyKind]);

  const loadGroupByReport = () => {
    if (kind !== 'layer' || !selectedId || !groupField) return;
    setLoading(true);
    setError(null);
    layersApi
      .getGroupByReport(selectedId, groupField, sumField || undefined)
      .then(setTable)
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Не удалось построить отчёт.'))
      .finally(() => setLoading(false));
  };

  const handleExportLayer = async () => {
    if (!selectedLayer) return;
    setExporting(true);
    try {
      const blob = await layersApi.exportLayerExcel(selectedLayer.id);
      downloadBlob(blob, `${selectedLayer.name}.xlsx`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось экспортировать слой.');
    } finally {
      setExporting(false);
    }
  };

  const handleExportReport = async () => {
    setExporting(true);
    try {
      const blob =
        kind === 'table' && selectedId
          ? energyKind === 'budget'
            ? await analyticalTablesApi.exportBudgetReportExcel(selectedId)
            : await analyticalTablesApi.exportEnergyReportExcel(selectedId, energyKind)
          : selectedLayer
            ? await layersApi.exportGroupByReportExcel(selectedLayer.id, groupField, sumField || undefined)
            : null;
      if (blob) downloadBlob(blob, `${selectedLayer?.name ?? analyticalTable?.name ?? 'отчёт'} — отчёт.xlsx`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось экспортировать отчёт.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <AppShell
      orgLabel={me?.workspace?.name ?? me?.workspace?.organization_name ?? '—'}
      navItems={NAV_ITEMS}
      backLabel="На карту"
      backTo="/app"
      title="Отчёты"
      breadcrumb="Личный кабинет / Отчёты"
      userInitials={me ? `${me.first_name?.[0] ?? ''}${me.last_name?.[0] ?? ''}`.toUpperCase() || '??' : '…'}
    >
      <p className="text-xs text-text-secondary">
        Выгрузка объектов слоя в Excel, сводные отчёты по группировке любого поля и отчёты по показаниям электроэнергии
        (аналитическая таблица, без геометрии).
      </p>

      <div className="mt-5 flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1.5">
          <label className="text-[11px] font-medium text-text-secondary">Слой или таблица</label>
          <select
            value={selection}
            onChange={(e) => setSelection(e.target.value)}
            className="h-10 w-80 rounded-[9px] border border-border-default bg-bg-surface px-3 text-[13px] text-text-primary outline-none focus:border-action-primary"
          >
            <option value="">— выберите —</option>
            <optgroup label="Слои">
              {layers.map((l) => (
                <option key={l.id} value={`layer:${l.id}`}>
                  {l.name} ({l.object_count.toLocaleString('ru-RU')})
                </option>
              ))}
            </optgroup>
            {analyticalTableList.length > 0 && (
              <optgroup label="Аналитические таблицы">
                {analyticalTableList.map((t) => (
                  <option key={t.id} value={`table:${t.id}`}>
                    {t.name}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
        </div>

        {selectedLayer && (
          <button
            type="button"
            onClick={handleExportLayer}
            disabled={exporting}
            className="flex h-10 items-center rounded-[10px] border border-border-default bg-bg-surface px-4 text-[13px] font-medium text-text-primary hover:bg-bg-surface-secondary disabled:cursor-not-allowed disabled:opacity-50"
          >
            {exporting ? 'Экспортируем…' : 'Экспорт слоя в Excel'}
          </button>
        )}
      </div>

      {isEnergyTable && (
        <div className="mt-5 flex flex-wrap items-center gap-2">
          {ENERGY_TABS.map((t) => (
            <button
              key={t.kind}
              type="button"
              onClick={() => setEnergyKind(t.kind)}
              className={`flex h-9 items-center rounded-lg px-3 text-[12px] font-medium ${
                energyKind === t.kind
                  ? 'bg-action-primary text-text-inverse'
                  : 'border border-border-default text-text-primary hover:bg-bg-surface-secondary'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {kind === 'table' && analyticalTable && (
        <div className="mt-5 rounded-2xl border border-border-default bg-bg-surface p-4">
          <div className="flex items-center justify-between">
            <p className="text-[13px] font-semibold text-text-primary">
              Записи ({rows.length.toLocaleString('ru-RU')})
            </p>
            {!addRowOpen && (
              <button
                type="button"
                onClick={() => setAddRowOpen(true)}
                className="text-[12px] font-medium text-action-primary hover:underline"
              >
                Добавить запись
              </button>
            )}
          </div>
          {addRowOpen && mapSettings?.id && (
            <AnalyticalRowForm
              table={analyticalTable}
              mapId={mapSettings.id}
              onCancel={() => setAddRowOpen(false)}
              onSaved={() => {
                setAddRowOpen(false);
                refreshRows();
              }}
            />
          )}
          {rows.length > 0 && (
            <div className="mt-3 overflow-x-auto">
              <table className="w-full border-collapse text-left text-xs">
                <thead className="bg-bg-surface-secondary">
                  <tr>
                    {analyticalTable.fields.map((f) => (
                      <th key={f.code} className="whitespace-nowrap px-3 py-2 font-semibold text-text-secondary">
                        {f.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-b border-border-default">
                      {analyticalTable.fields.map((f) => (
                        <td key={f.code} className="whitespace-nowrap px-3 py-1.5 text-text-primary">
                          {formatCell((r.attributes[f.code] as string | number | null) ?? null)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {kind === 'layer' && selectedLayer && (
        <div className="mt-5 flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1.5">
            <label className="text-[11px] font-medium text-text-secondary">Группировать по полю</label>
            <select
              value={groupField}
              onChange={(e) => setGroupField(e.target.value)}
              className="h-10 w-64 rounded-[9px] border border-border-default bg-bg-surface px-3 text-[13px] text-text-primary outline-none focus:border-action-primary"
            >
              <option value="">— выберите поле —</option>
              {fields.map((f) => (
                <option key={f.code} value={f.code}>
                  {f.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-[11px] font-medium text-text-secondary">Суммировать поле (необязательно)</label>
            <select
              value={sumField}
              onChange={(e) => setSumField(e.target.value)}
              className="h-10 w-64 rounded-[9px] border border-border-default bg-bg-surface px-3 text-[13px] text-text-primary outline-none focus:border-action-primary"
            >
              <option value="">— без суммы —</option>
              {numericFields.map((f) => (
                <option key={f.code} value={f.code}>
                  {f.name}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            onClick={loadGroupByReport}
            disabled={!groupField}
            className="flex h-10 items-center rounded-[10px] bg-action-primary px-4 text-[13px] font-medium text-text-inverse hover:bg-action-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            Построить отчёт
          </button>
        </div>
      )}

      {error && <p className="mt-4 text-xs font-medium text-status-danger">{error}</p>}

      {(table || loading) && (isEnergyTable || (kind === 'layer' && selectedLayer)) && (
        <div className="mt-5 flex-1 rounded-2xl border border-border-default bg-bg-surface p-4">
          <div className="flex items-center justify-between">
            <p className="text-[13px] font-semibold text-text-primary">
              {isEnergyTable
                ? ENERGY_TABS.find((t) => t.kind === energyKind)?.label
                : `Группировка по «${fields.find((f) => f.code === groupField)?.name ?? groupField}»`}
            </p>
            {table && table.rows.length > 0 && (
              <button
                type="button"
                onClick={handleExportReport}
                disabled={exporting}
                className="flex h-8 items-center rounded-lg border border-border-default px-3 text-[12px] font-medium text-text-primary hover:bg-bg-surface-secondary disabled:cursor-not-allowed disabled:opacity-50"
              >
                {exporting ? 'Экспортируем…' : 'Экспорт отчёта в Excel'}
              </button>
            )}
          </div>

          {loading ? (
            <p className="mt-4 text-xs text-text-secondary">Считаем…</p>
          ) : table && table.rows.length > 0 ? (
            <div className="mt-3 overflow-x-auto">
              <table className="w-full border-collapse text-left text-xs">
                <thead className="bg-bg-surface-secondary">
                  <tr>
                    {table.columns.map((c) => (
                      <th key={c} className="whitespace-nowrap px-3 py-2 font-semibold text-text-secondary">
                        {c}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {table.rows.map((row, i) => (
                    <tr key={i} className="border-b border-border-default">
                      {row.map((cell, j) => (
                        <td key={j} className="whitespace-nowrap px-3 py-1.5 text-text-primary">
                          {formatCell(cell)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="mt-4 text-xs text-text-secondary">Нет данных для отображения.</p>
          )}

          {energyKind === 'budget' && budgetTotals && budgetTotals.length > 0 && (
            <div className="mt-4 border-t border-border-default pt-3">
              <p className="text-[12px] font-semibold text-text-primary">Итого по бюджетам</p>
              <div className="mt-2 flex flex-wrap gap-4">
                {budgetTotals.map((t) => (
                  <div key={t.budget} className="rounded-lg bg-bg-surface-secondary px-3 py-2">
                    <p className="text-[11px] text-text-secondary">{t.budget}</p>
                    <p className="text-[13px] font-semibold text-text-primary">
                      {t.cost.toLocaleString('ru-RU')} ₽
                    </p>
                    <p className="text-[11px] text-text-secondary">{t.fact_kwh.toLocaleString('ru-RU')} кВт·ч</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </AppShell>
  );
}
